import { Controller } from "@hotwired/stimulus"

// Generates a looping CSS animation on image thumbnails that approximates
// the selected Ken Burns effect (zoom/pan) in real-time.
//
// Reads preset, intensity, and focal point from data attributes, builds
// CSS @keyframes dynamically, and applies them to the <img> element.
export default class extends Controller {
  static targets = ["image"]
  static values = {
    preset: { type: String, default: "zoom-in-center" },
    intensity: { type: Number, default: 0.5 },
    focalX: { type: Number, default: 0.5 },
    focalY: { type: Number, default: 0.5 },
    duration: { type: Number, default: 4 }
  }

  // Preset definitions mirroring kenBurnsPresets.ts
  static PRESETS = {
    "zoom-in-center":       { startZoom: 1.0,  endZoom: 1.15, startPanX: 0,     startPanY: 0,     endPanX: 0,     endPanY: 0    },
    "zoom-out-center":      { startZoom: 1.2,  endZoom: 1.0,  startPanX: 0,     startPanY: 0,     endPanX: 0,     endPanY: 0    },
    "pan-left-to-right":    { startZoom: 1.15, endZoom: 1.15, startPanX: -0.15, startPanY: 0,     endPanX: 0.15,  endPanY: 0    },
    "pan-right-to-left":    { startZoom: 1.15, endZoom: 1.15, startPanX: 0.15,  startPanY: 0,     endPanX: -0.15, endPanY: 0    },
    "pan-up-to-down":       { startZoom: 1.15, endZoom: 1.15, startPanX: 0,     startPanY: -0.12, endPanX: 0,     endPanY: 0.12 },
    "pan-down-to-up":       { startZoom: 1.15, endZoom: 1.15, startPanX: 0,     startPanY: 0.12,  endPanX: 0,     endPanY: -0.12 },
    "zoom-in-top-left":     { startZoom: 1.0,  endZoom: 1.25, startPanX: 0,     startPanY: 0,     endPanX: -0.15, endPanY: -0.1 },
    "zoom-in-bottom-right": { startZoom: 1.0,  endZoom: 1.25, startPanX: 0,     startPanY: 0,     endPanX: 0.15,  endPanY: 0.1  },
    "subtle-drift":         { startZoom: 1.02, endZoom: 1.08, startPanX: -0.02, startPanY: 0.02,  endPanX: 0.02,  endPanY: -0.02 }
  }

  connect() {
    this._styleEl = null
    this._animationName = `kb-preview-${this._uniqueId()}`
    this.applyAnimation()
  }

  disconnect() {
    this._removeStyleEl()
  }

  // Called when any value changes
  presetValueChanged()    { this.applyAnimation() }
  intensityValueChanged() { this.applyAnimation() }
  focalXValueChanged()    { this.applyAnimation() }
  focalYValueChanged()    { this.applyAnimation() }

  // Public method: update all values at once (called from external events)
  update({ detail } = {}) {
    if (!detail) return
    if (detail.preset !== undefined)    this.presetValue    = detail.preset
    if (detail.intensity !== undefined) this.intensityValue  = detail.intensity
    if (detail.focalX !== undefined)    this.focalXValue     = detail.focalX
    if (detail.focalY !== undefined)    this.focalYValue     = detail.focalY
  }

  applyAnimation() {
    if (!this.hasImageTarget) return

    const config = this._resolveConfig()
    if (!config) return

    this._removeStyleEl()
    this._injectKeyframes(config)
    this._applyToImage()
  }

  // Private

  _resolveConfig() {
    const base = this.constructor.PRESETS[this.presetValue]
    if (!base) return null

    // Apply intensity
    const intensity = this.intensityValue
    let config = this._applyIntensity(base, intensity)

    // Apply focal point
    const focalX = this.focalXValue
    const focalY = this.focalYValue
    if (focalX !== 0.5 || focalY !== 0.5) {
      config = this._applyFocalPoint(config, focalX, focalY)
    }

    return config
  }

  _applyIntensity(config, intensity) {
    if (intensity === 1.0) return { ...config }

    return {
      startZoom:  1.0 + (config.startZoom - 1.0) * intensity,
      endZoom:    1.0 + (config.endZoom - 1.0) * intensity,
      startPanX:  config.startPanX * intensity,
      startPanY:  config.startPanY * intensity,
      endPanX:    config.endPanX * intensity,
      endPanY:    config.endPanY * intensity,
    }
  }

  _applyFocalPoint(config, focalX, focalY) {
    const focalOffsetX = (focalX - 0.5) * 0.3
    const focalOffsetY = (focalY - 0.5) * 0.3

    const isZoomIn  = config.endZoom > config.startZoom
    const isZoomOut = config.startZoom > config.endZoom

    if (isZoomIn) {
      return { ...config, startPanX: 0, startPanY: 0, endPanX: -focalOffsetX, endPanY: -focalOffsetY }
    } else if (isZoomOut) {
      return { ...config, startPanX: -focalOffsetX, startPanY: -focalOffsetY, endPanX: 0, endPanY: 0 }
    }

    // Pan or drift: shift toward focal point
    return {
      ...config,
      startPanX: config.startPanX - focalOffsetX,
      startPanY: config.startPanY - focalOffsetY,
      endPanX:   config.endPanX - focalOffsetX,
      endPanY:   config.endPanY - focalOffsetY,
    }
  }

  _injectKeyframes(config) {
    // Convert pan fractions to percentage offsets (100 * pan)
    const startTx = (config.startPanX * 100).toFixed(2)
    const startTy = (config.startPanY * 100).toFixed(2)
    const endTx   = (config.endPanX * 100).toFixed(2)
    const endTy   = (config.endPanY * 100).toFixed(2)

    const css = `
      @keyframes ${this._animationName} {
        0%   { transform: scale(${config.startZoom.toFixed(4)}) translate(${startTx}%, ${startTy}%); }
        100% { transform: scale(${config.endZoom.toFixed(4)}) translate(${endTx}%, ${endTy}%); }
      }
    `
    this._styleEl = document.createElement("style")
    this._styleEl.textContent = css
    document.head.appendChild(this._styleEl)
  }

  _applyToImage() {
    const img = this.imageTarget
    img.style.animation = `${this._animationName} ${this.durationValue}s linear infinite alternate`
    img.style.willChange = "transform"
    img.style.transformOrigin = "center center"
  }

  _removeStyleEl() {
    if (this._styleEl) {
      this._styleEl.remove()
      this._styleEl = null
    }
    if (this.hasImageTarget) {
      this.imageTarget.style.animation = ""
      this.imageTarget.style.willChange = ""
    }
  }

  _uniqueId() {
    return Math.random().toString(36).substring(2, 9)
  }
}
